import React from 'react';
import { Facebook, Calendar, User, RefreshCw } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { FbPage } from '@/types/fb-type';

type ConnectedPageCardProp = {
    currentPage: FbPage;
    setIsOpen: (value: boolean) => void;
}

const ConnectedPageCard = ({ currentPage, setIsOpen }: ConnectedPageCardProp) => {
    const formattedDate = new Date(currentPage.createdAt).toLocaleString(undefined, {
        dateStyle: 'medium',
        timeStyle: 'short',
    });

    return (
        <div className="max-w-md mx-auto overflow-hidden bg-white border border-gray-200 rounded-2xl shadow-sm transition-all hover:shadow-md">
            <div className="p-8 text-center">
                {/* Profile Image & Brand Badge */}
                <div className="flex justify-center mb-6">
                    <div className="relative">
                        <div className="w-24 h-24 rounded-2xl overflow-hidden border-4 border-gray-50 shadow-inner bg-gray-100">
                            {currentPage.profileUrl ? (
                                <img
                                    src={currentPage.profileUrl}
                                    alt={currentPage.pageName}
                                    className="w-full h-full object-cover"
                                />
                            ) : (
                                <div className="w-full h-full flex items-center justify-center bg-blue-50">
                                    <Facebook className="w-10 h-10 text-blue-200" />
                                </div>
                            )}
                        </div>
                        {/* Facebook Brand Badge */}
                        <div className="absolute -bottom-2 -right-2 bg-blue-600 p-1.5 rounded-lg border-4 border-white shadow-sm">
                            <Facebook className="w-4 h-4 text-white fill-current" />
                        </div>
                    </div>
                </div>

                {/* Connection Details */}
                <div className="space-y-1">
                    <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-green-50 text-green-700 border border-green-100 mb-2">
                        <span className="relative flex h-2 w-2">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                            <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
                        </span>
                        <span className="text-xs font-bold uppercase tracking-wider">Connected</span>
                    </div>
                    <h3 className="text-2xl font-extrabold text-gray-900 tracking-tight">
                        {currentPage.pageName}
                    </h3>
                </div>

                {/* Metadata Grid */}
                <div className="mt-6 space-y-3 bg-gray-50 p-4 rounded-xl">
                    <div className="flex items-center justify-between text-sm">
                        <div className="flex items-center gap-2 text-gray-500">
                            <User className="w-4 h-4" />
                            <span>Admin</span>
                        </div>
                        <span className="font-semibold text-gray-900">{currentPage.connectedBy}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm border-t border-gray-200 pt-3">
                        <div className="flex items-center gap-2 text-gray-500">
                            <Calendar className="w-4 h-4" />
                            <span>Linked on</span>
                        </div>
                        <span className="font-medium text-gray-700">{formattedDate}</span>
                    </div>
                </div>

                {/* Action Button */}
                <div className="mt-8">
                    <Button
                        variant="secondary"
                        className="w-full h-11 flex items-center justify-center gap-2 font-semibold hover:bg-gray-200 transition-all active:scale-95"
                        onClick={() => setIsOpen(true)}
                    >
                        <RefreshCw className="w-4 h-4 text-gray-600" />
                        Change or Re-connect Page
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default ConnectedPageCard;